<div class="about-page">
  <section class="about-hero section section-no-border m-0">
    <div class="container py-5">
      <div class="row justify-content-center text-center">
        <div class="col-lg-10 col-xl-9">
          <span class="top-label">Built for private boat owners and growing charter operators</span>
          <h1 class="display-4 fw-bold text-color-dark mt-4 mb-4">
            About {{ title }}
          </h1>
          <p class="lead text-muted mb-0">
            {{ title }} helps serious operators replace reactive boat management with a more disciplined, professional rhythm.
            For private owners, that means protecting vessel value, staying ready for every trip, and removing the stress of scattered follow-up.
            For mid-level charter businesses, it means running cleaner turnarounds, stronger service standards, and a business guests trust faster.
          </p>
        </div>
      </div>
    </div>
  </section>

  <section class="section section-no-border bg-color-light m-0">
    <div class="container py-5">
      <div class="row align-items-center gy-4">
        <div _ngcontent-ng-c3356740947="" class="col-md-6"><img _ngcontent-ng-c3356740947="" src="https://cdn.pixabay.com/photo/2015/09/22/19/00/ship-952292_1280.jpg" alt="Boating" class="img-fluid rounded-4 shadow"></div>
        <div class="col-md-6">
          <span class="top-label">Our mission</span>
          <h2 class="word-rotator-title mb-3">Bring structure, confidence, and a stronger standard to every vessel you manage.</h2>
          <p class="text-muted">
            We built {{ title }} for owners and charter teams who have outgrown memory-based systems, scattered spreadsheets, and disconnected tools.
            When maintenance history, route planning, reminders, documents, and task ownership live in too many places, the operation feels heavier than it should.
          </p>
          <p class="text-muted mb-4">
            {{ title }} turns that friction into a clear operating flow, so your team can move faster, stay accountable, and present a noticeably more professional experience onshore and onboard.
          </p>
          <div class="mission-points">
            <div class="mission-point">
              <i class="bi bi-check-circle-fill"></i>
              <span>Keep readiness visible before small issues turn into lost time or avoidable cost.</span>
            </div>
            <div class="mission-point">
              <i class="bi bi-check-circle-fill"></i>
              <span>Give owners, captains, and crew one shared source of truth for daily execution.</span>
            </div>
            <div class="mission-point">
              <i class="bi bi-check-circle-fill"></i>
              <span>Create a business that feels more premium because the operation behind it is tighter.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="section m-0">
    <div class="container py-5">
      <div class="row justify-content-center text-center mb-5">
        <div class="col-lg-8">
          <span class="top-label">Who we serve</span>
          <h2 class="text-color-dark mt-3 mb-3">Designed for operators who want less friction and more control.</h2>
          <p class="text-muted mb-0">
            The platform is shaped around two audiences with the same expectation: their system should support the quality of the vessel, not undermine it.
          </p>
        </div>
      </div>

      <div class="row gy-5 align-items-start audience-row">
        <div class="col-lg-6">
          <div class="feature-box feature-box-style-2 align-items-start p-0">
            <div class="feature-box-icon me-4">
              <i class="bi bi-stars"></i>
            </div>
            <div class="feature-box-info">
              <h3 class="text-color-dark mb-3">Private boat owners</h3>
              <p class="text-muted mb-3">
                Protect your investment with a clear operational record, dependable service planning, and better trip readiness every time you leave the dock.
              </p>
              <ul class="detail-list mb-0">
                <li>Track maintenance, repairs, documents, and reminders without relying on memory.</li>
                <li>Prepare owner cruises and family outings with more certainty and less last-minute pressure.</li>
                <li>Maintain the kind of consistency that preserves vessel quality and owner confidence.</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="feature-box feature-box-style-2 align-items-start p-0">
            <div class="feature-box-icon me-4">
              <i class="bi bi-briefcase-fill"></i>
            </div>
            <div class="feature-box-info">
              <h3 class="text-color-dark mb-3">Mid-level charter businesses</h3>
              <p class="text-muted mb-3">
                Create a tighter charter workflow with stronger handovers, more repeatable standards, and an operation guests immediately perceive as more professional.
              </p>
              <ul class="detail-list mb-0">
                <li>Standardize task execution, readiness checks, and service follow-up across multiple boats.</li>
                <li>Improve accountability without slowing the team down with unnecessary admin weight.</li>
                <li>Build the operational backbone needed to scale without losing consistency.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="section section-secondary m-0">
    <div class="container py-5">
      <div class="row justify-content-center text-center mb-5">
        <div class="col-lg-8">
          <span class="top-label top-label-light">Why teams move to {{ title }}</span>
          <h2 class="text-white mt-3 mb-3">The payoff is operational clarity that people can feel quickly.</h2>
          <p class="text-white-50 mb-0">
            When every boat, task, route, checklist, reminder, and document is connected, the day stops feeling reactive and starts feeling controlled.
          </p>
        </div>
      </div>

      <div class="row gy-4 text-center benefit-row">
        <div class="col-md-4">
          <div class="benefit-item">
            <strong>Operate with confidence</strong>
            <p class="mb-0 text-white-50">Know what is complete, what is overdue, and what requires action next without chasing updates across different tools.</p>
          </div>
        </div>
        <div class="col-md-4">
          <div class="benefit-item">
            <strong>Elevate your reputation</strong>
            <p class="mb-0 text-white-50">Sharper systems create sharper owner, guest, and crew experiences that immediately strengthen trust in your operation.</p>
          </div>
        </div>
        <div class="col-md-4">
          <div class="benefit-item">
            <strong>Scale with discipline</strong>
            <p class="mb-0 text-white-50">Grow from a single vessel to a more demanding charter workflow without rebuilding your operating habits from scratch.</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="section m-0">
    <div class="container py-5">
      <div class="row align-items-center gy-4">
        <div class="col-lg-5">
          <span class="top-label">What makes {{ title }} different</span>
          <h2 class="text-color-dark mt-3 mb-3">This is more than software for logging work. It is a stronger way to run the business behind the boat.</h2>
          <p class="text-muted mb-0">
            Great marine operations are built on preparation, consistency, and trust. {{ title }} helps make those strengths repeatable whether you operate a private vessel or a charter business with growing complexity.
          </p>
        </div>
        <div class="col-lg-7">
          <div class="row gy-4 feature-list-grid">
            <div class="col-md-6">
              <div class="feature-list-item">
                <h4>One connected operating layer</h4>
                <p class="text-muted mb-0">Bring maintenance, routes, inventory, reminders, documents, and cruise planning into one reliable workflow.</p>
              </div>
            </div>
            <div class="col-md-6">
              <div class="feature-list-item">
                <h4>Cleaner team execution</h4>
                <p class="text-muted mb-0">Make handovers, responsibilities, and follow-up easier to manage across owners, captains, crew, and office staff.</p>
              </div>
            </div>
            <div class="col-md-6">
              <div class="feature-list-item">
                <h4>Earlier risk visibility</h4>
                <p class="text-muted mb-0">Spot overdue tasks, missing documents, and readiness gaps before they affect a trip, charter, or customer relationship.</p>
              </div>
            </div>
            <div class="col-md-6">
              <div class="feature-list-item">
                <h4>Professional growth foundation</h4>
                <p class="text-muted mb-0">Build an operation that looks more investable, more credible, and better prepared for the next level of demand.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="section section-no-border bg-color-light m-0">
    <div class="container py-5">
      <div class="row justify-content-center text-center">
        <div class="col-lg-9">
          <span class="top-label">Step into a more professional standard</span>
          <h2 class="text-color-dark mt-3 mb-3">Your boats already make an impression. Your operating system should reinforce it.</h2>
          <p class="text-muted mb-4">
            Whether you manage one high-value vessel or a charter business with multiple moving parts, {{ title }} gives you the structure to operate with more control, more momentum, and more credibility.
            That means fewer avoidable surprises, stronger daily execution, and a platform your team will not want to work without.
          </p>
          <div class="d-flex flex-column flex-sm-row justify-content-center gap-3">
            <a routerLink="/register" class="btn btn-primary btn-modern btn-lg px-5">Create your account</a>
            <a routerLink="/features" class="btn btn-outline btn-rounded btn-modern btn-lg px-5">Explore the platform</a>
          </div>
        </div>
      </div>
    </div>
  </section>
</div>
